import { useCallback, type ReactElement, type ReactNode } from "react";

import { isElectron } from "../env";
import { cn, isMacPlatform } from "~/lib/utils";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import {
  APP_TOP_BAR_CLASS,
  APP_TOP_BAR_STYLE,
  APP_TOP_BAR_LEFT_ZONE_CLASS,
  APP_TOP_BAR_LOGO_BUTTON_CLASS,
} from "./appTopBarLayout";
import { APP_SHELL_COLUMN_LAYOUT_CLASS } from "./appShellLayout";
import { AppTopBarActionSlotTarget } from "./appTopBarActionSlot";
import { AppTopBarLogo } from "./branding/AppTopBarLogo";
import { useLocation } from "@tanstack/react-router";
import { GlobalBreadcrumb } from "./breadcrumb/GlobalBreadcrumb";
import { useGlobalBreadcrumbCrumbs } from "./breadcrumb/useGlobalBreadcrumbCrumbs";
import {
  selectPublishedLeafActions,
  useBreadcrumbLeafActionsStore,
} from "./breadcrumb/breadcrumbLeafActionsStore";
import {
  selectPublishedDetailForPath,
  useGlobalBreadcrumbStore,
} from "./breadcrumb/globalBreadcrumbStore";
import { MACOS_TRAFFIC_LIGHTS_INSET_CLASS } from "./sidebar/SidebarBrandHeader";
import { SidebarTrigger, useOptionalSidebar } from "./ui/sidebar";

interface AppTopBarProps {
  readonly rightSlot?: ReactNode;
  /**
   * Off for surfaces rendered outside the app shell (login, pairing, the router
   * error boundary), where there is no route path worth naming.
   */
  readonly showBreadcrumb?: boolean;
  /**
   * On only for sidebar-less shells. In the app the wordmark lives in the
   * sidebar header; login and pairing have no sidebar, and those are exactly the
   * screens where the product still has to say what it is.
   */
  readonly showWordmark?: boolean;
  readonly showNavigationControls?: boolean;
}

interface AppTopBarShellProps {
  readonly children: ReactNode;
  readonly rightSlot?: ReactNode;
  readonly showBreadcrumb?: boolean;
  readonly showWordmark?: boolean;
  readonly showNavigationControls?: boolean;
}

export function resolveAppTopBarNewChatHref(input: {
  readonly href: string;
  readonly isElectronShell: boolean;
  readonly origin: string;
}): string {
  if (input.isElectronShell) {
    const url = new URL(input.href);
    url.hash = "/new";
    return url.toString();
  }

  return new URL("/new", input.origin).toString();
}

/**
 * The bar reserves space for the macOS traffic lights only when nothing else
 * does. While the sidebar is open its header holds that inset, and duplicating
 * it here would indent the breadcrumb by 88px for no reason.
 */
export function resolveAppTopBarLeftControlsClassName(input: {
  readonly reserveMacTrafficLightSpace: boolean;
}): string {
  return cn(
    APP_TOP_BAR_LEFT_ZONE_CLASS,
    input.reserveMacTrafficLightSpace ? MACOS_TRAFFIC_LIGHTS_INSET_CLASS : "pl-1 md:pl-2.5",
  );
}

export function AppTopBarShell({
  children,
  rightSlot,
  showBreadcrumb = false,
  showWordmark = true,
  showNavigationControls = false,
}: AppTopBarShellProps): ReactElement {
  return (
    <div className={APP_SHELL_COLUMN_LAYOUT_CLASS}>
      <AppTopBar
        rightSlot={rightSlot}
        showBreadcrumb={showBreadcrumb}
        showNavigationControls={showNavigationControls}
        showWordmark={showWordmark}
      />
      <div className="flex h-[var(--forge-content-height)] min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

export function AppTopBar({
  rightSlot,
  showBreadcrumb = true,
  showWordmark = false,
  showNavigationControls = true,
}: AppTopBarProps): ReactElement {
  const sidebar = useOptionalSidebar();
  // While the sidebar is open its own header carries the trigger, at the same
  // screen position. Rendering both would double the control; rendering neither
  // when collapsed would strand the user with no way back.
  const isSidebarHidden = sidebar === null || sidebar.isMobile || sidebar.state === "collapsed";
  const shouldShowTrigger = showNavigationControls && sidebar !== null && isSidebarHidden;
  const shouldReserveMacTrafficLightSpace =
    isElectron &&
    typeof navigator !== "undefined" &&
    isMacPlatform(navigator.platform) &&
    isSidebarHidden;

  const handleLogoClick = useCallback(() => {
    window.location.assign(
      resolveAppTopBarNewChatHref({
        href: window.location.href,
        isElectronShell: isElectron,
        origin: window.location.origin,
      }),
    );
  }, []);

  return (
    <header className={APP_TOP_BAR_CLASS} style={APP_TOP_BAR_STYLE} data-testid="app-top-bar">
      <div
        className={resolveAppTopBarLeftControlsClassName({
          reserveMacTrafficLightSpace: shouldReserveMacTrafficLightSpace,
        })}
        data-testid="app-top-bar-left"
      >
        {shouldShowTrigger ? (
          <SidebarTrigger
            aria-label="Toggle sidebar"
            className={cn(
              RESPONSIVE_ICON_BUTTON_CLASS,
              "shrink-0 text-muted-foreground hover:text-foreground",
            )}
          />
        ) : null}
        {showWordmark ? (
          <button
            aria-label="Go to new chat"
            className={cn(APP_TOP_BAR_LOGO_BUTTON_CLASS, "cursor-pointer hover:bg-accent")}
            data-testid="app-top-bar-logo"
            onClick={handleLogoClick}
            type="button"
          >
            <AppTopBarLogo />
          </button>
        ) : null}
        {showBreadcrumb ? <AppTopBarBreadcrumb /> : null}
      </div>
      {/* Rendered even when empty so the grid keeps a stable second column and
          the breadcrumb's truncation point does not shift as actions mount.
          `AppTopBarActionSlotTarget` is where routes portal their own controls
          (see `appTopBarActionSlot`), which is what lets a page carry one
          persistent control without growing a header row to hold it.

          Draggable like the rest of the bar (see the drag contract in
          `appTopBarLayout`): the controls that land here are buttons, which
          `index.css` already exempts, so blanket-exempting the zone only cost
          the gaps between them. */}
      <div
        className="flex min-w-0 items-center justify-end gap-0.5 pr-1 md:gap-1 md:pr-2.5"
        data-testid="app-top-bar-right"
      >
        {rightSlot}
        <AppTopBarActionSlotTarget />
      </div>
    </header>
  );
}

/**
 * Separated so the router-dependent hook is never called on the surfaces that
 * render the bar without a live router (the error boundary, notably).
 */
function AppTopBarBreadcrumb(): ReactElement | null {
  const crumbs = useGlobalBreadcrumbCrumbs();
  // Read, never built here. What the leaf can do needs `useThreadActions` and
  // therefore a QueryClient, which this component cannot assume it has — the
  // reason it is split out at all. The thread route publishes; the bar reads.
  const pathname = useLocation({ select: (location) => location.pathname });
  const leafActions = useBreadcrumbLeafActionsStore((store) =>
    selectPublishedLeafActions(store, pathname),
  );
  // Selected as a plain string, not wrapped: this selector runs on a component
  // mounted over every route, and one that builds a fresh object re-renders
  // forever (see the warning in `useGlobalBreadcrumbCrumbs`).
  const detail = useGlobalBreadcrumbStore((store) => selectPublishedDetailForPath(store, pathname));
  return (
    <GlobalBreadcrumb
      crumbs={crumbs}
      detail={detail ?? undefined}
      leafActions={leafActions ?? undefined}
    />
  );
}
